/**
 * useOnboardingStatus — fetches GET /api/v1/onboarding/status once per
 * session and caches in a module-level Promise to dedupe concurrent
 * calls from multiple consumers (router guard + ChoosePath both ask
 * for it on first render).
 *
 * Exports:
 *   useOnboardingStatus()  — { status, isLoading, error, refetch }
 *
 * The `status` object shape mirrors the BE response payload:
 *   { has_chosen_subscription_path: bool, is_trial: bool,
 *     trial_end_date: string|null, trial_preview: { ... } }
 *
 * Call refetch() after the user picks Free Trial or completes a paid
 * purchase so the cached flag flips and the guard stops redirecting.
 */
import { useCallback, useEffect, useState } from "react";
import { fetchData } from "../api/apiService";
import { ONBOARDING } from "../api/endpointMap";

let _statusPromise = null;
let _statusCache = null;

function _unwrap(raw) {
  return raw?.result || raw?.data || raw || {};
}

function _load(force = false) {
  if (force) {
    _statusPromise = null;
    _statusCache = null;
  }
  if (_statusPromise) return _statusPromise;
  _statusPromise = fetchData(ONBOARDING.STATUS)
    .then((raw) => {
      _statusCache = _unwrap(raw);
      return _statusCache;
    })
    .catch((err) => {
      _statusPromise = null;
      throw err;
    });
  return _statusPromise;
}

export function resetOnboardingStatusCache() {
  _statusPromise = null;
  _statusCache = null;
}

export function useOnboardingStatus() {
  const [state, setState] = useState({
    status: _statusCache,
    isLoading: !_statusCache,
    error: null,
  });

  const refetch = useCallback(() => {
    setState((s) => ({ ...s, isLoading: true, error: null }));
    _load(true)
      .then((status) => setState({ status, isLoading: false, error: null }))
      .catch((err) =>
        setState((s) => ({
          ...s,
          isLoading: false,
          error: err?.detail || err?.message || "Failed to load onboarding status",
        })),
      );
  }, []);

  useEffect(() => {
    let cancelled = false;
    _load()
      .then((status) => {
        if (!cancelled) setState({ status, isLoading: false, error: null });
      })
      .catch((err) => {
        if (!cancelled) {
          setState((s) => ({
            ...s,
            isLoading: false,
            error: err?.detail || err?.message || "Failed to load onboarding status",
          }));
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { ...state, refetch };
}
